'use client';

import type { EditorView } from '@tiptap/pm/view';
import { AnyExtension, EditorContent, useEditor } from '@tiptap/react';

import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { debounce } from '@/utils/common';
import { cn } from '@/utils/styles';

import { RichTextEditorProvider, useRichTextEditor } from './rich-text-editor-provider';
import Toolbar from './toolbar';
import {
	IRichTextEditorProps,
	RTE_EXTENSIONS,
	UploadImageFn,
	VariableProps,
} from './types/rich-text-editor';
import { trimEditorViewToHtmlLength } from './utils/character-limit';
import { RteExtensionMap } from './utils/extensions';
import { stripTrailingEmptyParagraphs } from './utils/extraction';
import { cleanPastedHtml } from './utils/paste';
import { YoutubeEmbedDialog } from './youtube';

export interface RichTextEditorRef {
	focus: () => void;
	blur: () => void;
	clearContent: () => void;
	setContent: (content: string) => void;
	/** Insert a variable node at the current cursor position. */
	addVariable: (variable: { id: string; token: string; label: string; prefix?: string }) => void;
	/** Remove the first variable node matching the given id from the editor document. */
	removeVariable: (id: string) => void;
}
export const allExtensions = Object.keys(RteExtensionMap) as RTE_EXTENSIONS[];

const EDITOR_CONTENT_BASE_CLASS = `bg-transparent p-0 focus-visible:ring-transparent
	focus-visible:ring-offset-0 disabled:cursor-default disabled:opacity-100 rounded-none
	border-none outline-none min-h-7 text-base text-primary leading-normal cursor-text`;

const EMPTY_VARIABLE_PROPS: VariableProps = { items: [] };
const EMPTY_ADDITIONAL_VARIABLE_PROPS: VariableProps[] = [];

/** Tiptap keeps trailing empty paragraphs the API drops — ignore that difference when comparing. */
const normalizeHtml = (html?: string) => stripTrailingEmptyParagraphs((html ?? '').trim()).trim();

const RichTextEditor = forwardRef<RichTextEditorRef, IRichTextEditorProps>(({ ...props }, ref) => {
	return (
		<RichTextEditorProvider editMode={props.editMode}>
			<RichTextEditorMain
				ref={ref}
				{...props}
			/>
		</RichTextEditorProvider>
	);
});

const RichTextEditorMain = forwardRef<RichTextEditorRef, IRichTextEditorProps>(
	(
		{
			description,
			onChangeDescription,
			editMode,
			onFocus,
			onBlur,
			isFocusedField,
			debounceDuration,
			onKeyDown,
			allowedExtensions = allExtensions,
			mentionProps = { items: [] },
			variableProps = EMPTY_VARIABLE_PROPS,
			additionalVariableProps = EMPTY_ADDITIONAL_VARIABLE_PROPS,
			placeholder,
			className,
			contentClassName,
			hideToolbar = false,
			stopOnChangeDebounce = false,
			// New prop with default
			hideFocusUnderline = false,
			onPaste,
			hiddenToolbarOptions,
			singleLine = false,
			uploadImage,
			starterKitOptions,
			maxHtmlLengthOnBlur,
			onCharacterLimitExceeded,
		},
		ref
	) => {
		const { setMentionProps, setVariableProps } = useRichTextEditor();
		const [content, setContent] = useState(description);
		const [isEditorFocused, setIsEditorFocused] = useState(false);
		const [isYoutubeDialogOpen, setIsYoutubeDialogOpen] = useState(false);
		/** True while a debounced save is still in flight, so the incoming prop is stale. */
		const hasPendingSaveRef = useRef(false);
		/** The editor is built once, so the extension must read the latest uploader, not the mount-time one. */
		const uploadImageRef = useRef(uploadImage);

		useEffect(() => {
			uploadImageRef.current = uploadImage;
		}, [uploadImage]);

		const stableUploadImage = useCallback<UploadImageFn>(
			(file, onProgress) =>
				uploadImageRef.current?.(file, onProgress) ??
				Promise.reject(new Error('The image uploader was removed mid-upload')),
			[]
		);

		const openYoutubeDialog = useCallback(() => setIsYoutubeDialogOpen(true), []);

		// eslint-disable-next-line react-hooks/exhaustive-deps
		const handleChangeValue = useCallback(
			debounce((value) => {
				hasPendingSaveRef.current = false;
				onChangeDescription?.(value as string);
			}, debounceDuration ?? 250),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[onChangeDescription]
		);

		// Send a pending edit now rather than after the editor is gone; a layout cleanup
		// runs before the form editor's passive cleanup drops its cache.
		useLayoutEffect(() => handleChangeValue.flush, [handleChangeValue]);

		const handleFocus = () => {
			setIsEditorFocused(true);
			onFocus?.();
		};

		const handleBlur = (view: EditorView) => {
			const characterLimit = maxHtmlLengthOnBlur ?? 0;
			const wasTrimmed =
				characterLimit > 0
					? trimEditorViewToHtmlLength(view, characterLimit)
					: false;

			if (wasTrimmed) onCharacterLimitExceeded?.(characterLimit);
			setIsEditorFocused(false);
			onBlur?.();
			return false;
		};

		const editor = useEditor(
			{
				extensions: allowedExtensions.flatMap((extension: RTE_EXTENSIONS) => {
					if (extension !== RTE_EXTENSIONS.VARIABLE) {
						return [
							RteExtensionMap[extension]({
								placeholder,
								variables: variableProps?.items,
								getVariableTooltip: variableProps?.getTooltip,
								triggerChar: variableProps?.triggerChar,
								// Presence is read once at build time, so pass it from the first render or not at all.
								uploadImage: uploadImage ? stableUploadImage : undefined,
								onRequestYoutubeUrl: openYoutubeDialog,
								starterKitOptions,
							}) as AnyExtension,
						];
					}

					const variableConfigs = [variableProps, ...additionalVariableProps];

					return variableConfigs.map(
						(config, index) =>
							RteExtensionMap[RTE_EXTENSIONS.VARIABLE]({
								placeholder,
								variables: config.items,
								getVariableTooltip: config.getTooltip,
								triggerChar: config.triggerChar,
								name: index === 0 ? 'variableExtension' : `variableExtension-${index}`,
								pluginKeyName: index === 0 ? 'variable-suggestion' : `variable-suggestion-${index}`,
								includeVariableNode: index === 0,
								variableProps: config,
							}) as AnyExtension
					);
				}),
				content,
				editable: !!editMode,
				...(onPaste ? { onPaste } : {}),
				editorProps: {
					transformPastedHTML: cleanPastedHtml,
					...(singleLine
						? {
								// Block Enter so the field stays one line, but let the variable
								// suggestion popup consume Enter (arrow+Enter to pick) — its active
								// decoration is only in the DOM while the popup is open.
								handleKeyDown: (view: EditorView, event: KeyboardEvent) => {
									if (event.key !== 'Enter') return false;
									return !view.dom.querySelector('.suggestion');
								},
								// Paste as plain text with newlines flattened to spaces.
								handlePaste: (view: EditorView, event: ClipboardEvent) => {
									const text = event.clipboardData?.getData('text/plain') ?? '';
									const flattened = text.replace(/\s*[\r\n]+\s*/g, ' ').replace(/\u00A0/g, ' ');
									view.dispatch(view.state.tr.insertText(flattened));
									return true;
								},
							}
						: {}),
					attributes: { class: cn(EDITOR_CONTENT_BASE_CLASS, contentClassName) },
					handleDOMEvents: {
						blur: handleBlur,
						focus: handleFocus,
					},
				},
				onUpdate: ({ editor }) => {
					const updatedRichText = editor.getHTML();
					const isEmpty = editor.isEmpty;
					setContent(updatedRichText);
					const finalValue = !isEmpty ? updatedRichText : '';
					hasPendingSaveRef.current = !stopOnChangeDebounce;

					if (stopOnChangeDebounce) {
						onChangeDescription?.(finalValue);
					} else {
						handleChangeValue(finalValue);
					}
				},
				enableCoreExtensions: true,
				immediatelyRender: false,
			},
			[variableProps, additionalVariableProps]
		);

		// The deps array above freezes `editorProps`; go through `setOptions` so the later
		// `setEditable` re-applies this class rather than the stale one.
		useEffect(() => {
			if (!editor) return;
			editor.setOptions({
				editorProps: {
					...editor.options.editorProps,
					attributes: { class: cn(EDITOR_CONTENT_BASE_CLASS, contentClassName) },
				},
			});
		}, [editor, contentClassName]);

		// Only external changes (AI agent, refetch) may overwrite the editor — never our own echo, never mid-edit.
		useEffect(() => {
			if (!editor) return;

			if (normalizeHtml(description) === normalizeHtml(editor.getHTML())) return;
			if (isEditorFocused || hasPendingSaveRef.current) return;

			setContent(description);
			editor.commands.setContent(description, false);
		}, [description, editor, isEditorFocused]);

		useImperativeHandle(
			ref,
			() => ({
				focus: () => {
					editor?.commands?.focus('end');
				},
				blur: () => {
					editor?.commands?.blur();
				},
				clearContent: () => {
					editor?.commands?.clearContent();
					setContent('');
				},
				setContent: (newVal: string) => {
					if (content !== newVal) {
						setContent(newVal);
						editor?.commands.setContent(newVal, false);
					}
				},
				addVariable: (variable: { id: string; token: string; label: string; prefix?: string }) => {
					if (!editor) return;
					const { state } = editor;
					const variableNode = state.schema.nodes.variable?.create(variable);
					if (!variableNode) return;
					const { from } = state.selection;
					const tr = state.tr.insert(from, variableNode);
					editor.view.dispatch(tr);
				},
				removeVariable: (id: string) => {
					if (!editor) return;
					const { state } = editor;
					const tr = state.tr;
					let deleted = false;
					state.doc.descendants((node, pos) => {
						if (deleted) return false;
						if (node.type.name === 'variable' && node.attrs.id === id) {
							tr.delete(pos, pos + node.nodeSize);
							deleted = true;
							return false;
						}
					});
					if (deleted) editor.view.dispatch(tr);
				},
			}),
			[editor, content]
		);

		const handleInsertYoutube = useCallback(
			(url: string) => {
				editor?.chain().focus().setYoutubeVideo({ src: url }).run();
			},
			[editor]
		);

		const handleContainerFocus = () => {
			onFocus?.();
		};

		// The editable area can be shorter than the container (a tall `min-h-*` wrapper
		// around a near-empty doc), so a click in that gap would otherwise land on the
		// container and do nothing. Put the caret in the editor instead.
		const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
			onFocus?.();

			if (!editor || !editMode || editor.isFocused) return;
			if (event.target !== event.currentTarget) return;

			editor.commands.focus('end');
		};

		const handleContainerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
			const isModifierKeyPressed = event.shiftKey || event.ctrlKey || event.altKey || event.metaKey;

			if (event.key === 'Enter' && !isModifierKeyPressed) {
				onFocus?.();
			}

			onKeyDown?.(event);
		};

		useEffect(() => {
			if (mentionProps) setMentionProps(mentionProps);
		}, [mentionProps?.items?.length]);

		useEffect(() => {
			if (variableProps) setVariableProps(variableProps);
		}, [
			variableProps?.items?.length,
			variableProps?.getTooltip,
			variableProps?.triggerChar,
			variableProps?.showBrackets,
			variableProps?.searchItems,
		]);

		// Update editor editable state when editMode changes
		useEffect(() => {
			if (editor) {
				editor.setEditable(!!editMode);
			}
		}, [editMode]);

		return (
			<div
				className={cn(
					'word-break flex w-full flex-col outline-none transition-all duration-200 ease-in-out',
					!editMode && 'cursor-default focus-visible:outline-none',
					className
				)}
				role="button"
				tabIndex={0}
				onClick={handleContainerClick}
				onFocus={handleContainerFocus}
				onKeyDown={handleContainerKeyDown}
			>
				{!hideToolbar && (
					<Toolbar
						editor={editor}
						allowedExtensions={allowedExtensions}
						hiddenToolbarOptions={hiddenToolbarOptions}
					/>
				)}
				<EditorContent
					editor={editor}
					className="flex flex-1 flex-col [&>.tiptap]:flex-1"
				/>
				{allowedExtensions.includes(RTE_EXTENSIONS.YOUTUBE) && (
					<YoutubeEmbedDialog
						open={isYoutubeDialogOpen}
						onOpenChange={setIsYoutubeDialogOpen}
						onSubmit={handleInsertYoutube}
					/>
				)}
				{!hideFocusUnderline && (
					<div
						className={`h-0 w-full bg-slate-700 transition-transform duration-300 ease-out ${
							isFocusedField ? 'mb-2 h-0.5 scale-x-100' : 'mb-0 h-0 scale-x-0'
						} origin-left`}
					/>
				)}
			</div>
		);
	}
);

RichTextEditor.displayName = 'RichTextEditor';
RichTextEditorMain.displayName = 'RichTextEditorMain';

export default RichTextEditor;
